// src/content/assistants.js
//
// Content file for the in-app "Connect your AI assistant" panel (rendered as tabs).
// Plain data only: no JSX, no imports. Full documentation: docs/mcp.md
//
// TODO(K8): approve wording
//
// Vendor steps checked on 2026-10-12 against:
//   Mistral Vibe (formerly Le Chat): https://docs.mistral.ai/vibe/work/connectors/mcp-connectors
//     rename: https://en.wikipedia.org/wiki/Mistral_Vibe
//     authless via web form (user report): https://github.com/mistralai/mistral-vibe/issues/1218
//   Claude: https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-MCP
//   ChatGPT: https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt
//     Plugins-page flow (Oct 2026): https://community.openai.com/t/developer-mode-missing-and-mcp-app-creation-unavailable-across-multiple-chatgpt-accounts/1402294
//                                   https://github.com/Miyamura80/MCP-Template/pull/242
//   Other clients: https://modelcontextprotocol.io/clients  (TODO(K8): verify URL)
//   MCP Inspector: https://github.com/modelcontextprotocol/inspector
//
// TODO(K8): verify - ChatGPT menu labels / plan matrix were changing in Oct 2026;
//           Mistral in-chat "Tools" toggle and no-auth option label not confirmed first-hand.

export const ASSISTANT_GUIDES_CHECKED = "2026-10-12";

export const MCP_SERVER_URL = "https://learning-journey.k8.design/mcp";

export const ASSISTANT_TABS = [
  {
    id: "mistral",
    name: "Mistral Vibe",
    subtitle: "formerly Le Chat",
    recommended: true,
    badge: "Recommended · EU",
    steps: [
      "In Mistral Vibe, open Connectors from the sidebar.",
      "Click “+ Add Connector” and choose the “Custom MCP Connector” tab.",
      "Enter a name without spaces and paste the server URL, then click Connect.",
      "In a chat, turn the connector on under Tools and paste your assistant code.",
    ],
    planNote:
      "Adding a custom connector is an admin feature. On Free, Pro and Student plans you are the admin. On Team plans, ask your admin to add it. No sign-in is needed.",
    helpUrl: "https://docs.mistral.ai/vibe/work/connectors/mcp-connectors",
    helpLabel: "Mistral help: custom connectors",
  },
  {
    id: "claude",
    name: "Claude",
    subtitle: "claude.ai, desktop and mobile apps",
    recommended: false,
    badge: null,
    steps: [
      "Go to Customize > Connectors, click “+ Add”, then “Add custom connector”.",
      "Enter a name and paste the server URL, then click Continue.",
      "Under Authentication choose “No sign in”, then click Add.",
      "In a chat, click + > Connectors, switch it on and paste your assistant code.",
    ],
    planNote:
      "Free, Pro, Max, Team and Enterprise (Free: one custom connector). On Team and Enterprise an Owner adds it under Organization settings > Connectors.",
    helpUrl:
      "https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-MCP",
    helpLabel: "Claude help: custom connectors",
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    subtitle: "custom MCP server",
    recommended: false,
    badge: null,
    steps: [
      "Open chatgpt.com/plugins and click Add > “Create custom MCP server”.",
      "Paste the server URL and choose “No authentication”.",
      "Accept the notice and click “Create as a plugin”.",
      "In a chat, select the plugin and paste your assistant code.",
    ],
    planNote:
      "Not available on Free. Plus, Pro, Business, Enterprise and Edu. On some plans ChatGPT allows read-only use, and on work plans an admin may need to allow custom connectors first. Menu names were changing in October 2026.",
    helpUrl: "https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt",
    helpLabel: "OpenAI help: MCP apps in ChatGPT",
  },
  {
    id: "other",
    name: "Other apps",
    subtitle: "any MCP client with remote HTTP support",
    recommended: false,
    badge: null,
    steps: [
      "Open the app’s MCP or connector settings and add a new server.",
      "Choose “HTTP” or “Streamable HTTP” and paste the server URL.",
      "Leave authentication empty, save, and paste your assistant code into the chat.",
    ],
    planNote: "Works with clients that support remote Streamable HTTP servers, e.g. Claude Code, VS Code, Cursor.",
    helpUrl: "https://modelcontextprotocol.io/clients",
    helpLabel: "MCP: list of compatible apps",
  },
  {
    id: "none",
    name: "No connector?",
    subtitle: "copy and paste with any chatbot",
    recommended: false,
    badge: null,
    steps: [
      "Use “Prepare with any AI chatbot” on steps 1–3 to copy a ready-made prompt.",
      "Paste it into any chatbot and answer its questions.",
      "Copy the chatbot’s answer and use “Import answer” in the tool.",
      "Review the values and press Confirm.",
    ],
    planNote: "Works with any chatbot, no setup needed.",
    helpUrl: null,
    helpLabel: null,
  },
];

export const EXAMPLE_PROMPTS = {
  startInAssistant:
    "Use the T4R Learning Journey connector to help me fill in steps 1–4 for a mobility-focused journey for Utrecht. Ask me questions one step at a time, then give me the review link.",
  withCode: (code) =>
    `Here is my T4R assistant code: ${code}. Use the T4R Learning Journey connector to check which fields are still missing and help me complete them.`,
  step4:
    "Here is our learner's CV. Use the T4R connector to send a short summary of what they already know for step 4 (skills and experience only, no personal data). The tool will mark the matching MLUs.",
};

export const IN_EVERY_PATH = [
  "You decide: nothing counts until you press Confirm in the tool.",
  "Steps 5 and 7 always run inside the tool.",
  "What your own assistant sees is governed by that assistant's terms, not K8's.",
];
